import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { ConnectedProjectDto, SystemGroupCreateRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createGroupFormPermissions, toGroupPermissionsRequest } from "@/helpers/system-groups/permissions";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { platformGroupMutations } from "@/queries/platform-groups/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type FormValues, PlatformGroupForm } from "./PlatformGroupForm";

export function CreatePlatformGroupPage(): React.ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const { data: projects = [] } = useLinkedProjects();
  const permissionQueries = usePermissionQueries();
  const defaultPermissionsQuery = useQuery(permissionQueries.listDefaultTypes());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const createGroup = platformGroupMutations.useCreateGroup();

  const error = defaultPermissionsQuery.error || rolesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (defaultPermissionsQuery.isFetching || rolesQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const handleSubmit = async (values: FormValues) => {
    if (!values.image?.length) {
      showFlashToast({ variant: "alert", message: t("page.platform-groups.create.error") });

      return;
    }
    const image = await uploadFormImage(values.image[0]);
    if (!image) {
      return;
    }
    const payload: SystemGroupCreateRequest = {
      type: values.type,
      name: values.name,
      description: values.description,
      icon: values.icon!,
      imageId: image.id,
      defaultPostingAudience: values.audience,
      permissions: toGroupPermissionsRequest(values.permissions),
      excludedProjectIds: getExcludedProjectIds(projects, values.projects),
      postingLevel: values.postingLevel,
    };
    await createGroup.mutateAsync(payload);
    void navigate(routes.platformGroups.list());
  };

  return (
    <PlatformGroupForm
      isEditMode={false}
      projects={projects}
      defaultValues={{
        type: "interest",
        name: "",
        description: "",
        icon: undefined,
        image: [],
        projects,
        postingLevel: "projectAndProjectConnection",
        audience: "projectConnection",
        permissions: createGroupFormPermissions(rolesQuery.data ?? [], defaultPermissionsQuery.data ?? [], undefined),
      }}
      onSubmit={handleSubmit}
      isSubmitting={isUploadingImage || createGroup.isPending}
    />
  );
}

function getExcludedProjectIds(projects: ConnectedProjectDto[], enabledProjects: ConnectedProjectDto[]): string[] {
  const enabledProjectIds = enabledProjects.map((project) => project.id);

  return projects.map((project) => project.id).filter((projectId) => !enabledProjectIds.includes(projectId));
}
